import React, { useCallback, useEffect, useRef, useState } from 'react';

function UserManagement({ apiBaseUrl, currentUser, screen = 'account', compactLogin = false, requestedAuthMode, onAuthChange, onLogout, onSelectStock, onPreferredStocksChange, darkTheme }) {
  const formatRoleLabel = (role) => {
    const text = String(role || '').trim();
    if (text.toLowerCase() === 'start') return 'Start (free)';
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
  };
  const registrationTiers = ['start', 'smart', 'pro', 'elite', 'quantum'];
  const paidRegistrationTiers = ['smart', 'pro', 'elite', 'quantum'];

  const [mode, setMode] = useState('login');
  const [authForm, setAuthForm] = useState({ username: '', email: '', password: '', role: 'start' });
  const [authMessage, setAuthMessage] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({ username: '', email: '', currentPassword: '', newPassword: '', confirmPassword: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [preferredStocks, setPreferredStocks] = useState([]);
  const [preferredInput, setPreferredInput] = useState('');
  const [preferredLoading, setPreferredLoading] = useState(false);
  const [preferredError, setPreferredError] = useState('');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [availableRoles, setAvailableRoles] = useState(['admin', 'start']);
  const paymentSessionHandledRef = useRef(null);
  const isAdminScreen = screen === 'admin';
  const isAccountScreen = !isAdminScreen;
  const isCompactLogin = Boolean(compactLogin && isAccountScreen && !currentUser?.token);
  const [moduleTab, setModuleTab] = useState(isAdminScreen ? 'admin' : currentUser?.token ? 'stocks' : 'profile');

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      ...(currentUser?.token ? { Authorization: `Bearer ${currentUser.token}` } : {})
    }),
    [currentUser?.token]
  );

  const fetchPreferredStocks = useCallback(async () => {
    if (!currentUser?.token) return;
    setPreferredLoading(true);
    setPreferredError('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/preferred-stocks`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load preferred stocks');
      const nextPreferredStocks = data.preferred_stocks || [];
      setPreferredStocks(nextPreferredStocks);
      if (onPreferredStocksChange) onPreferredStocksChange(nextPreferredStocks);
    } catch (error) {
      setPreferredError(error.message);
    } finally {
      setPreferredLoading(false);
    }
  }, [apiBaseUrl, authHeaders, currentUser?.token, onPreferredStocksChange]);

  const fetchAdminUsers = useCallback(async () => {
    if (!currentUser?.token || currentUser.role !== 'admin') return;
    setAdminLoading(true);
    setAdminError('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/users`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load users');
      setAdminUsers(data.users || []);
    } catch (error) {
      setAdminError(error.message);
    } finally {
      setAdminLoading(false);
    }
  }, [apiBaseUrl, authHeaders, currentUser?.role, currentUser?.token]);

  const fetchRoles = useCallback(async () => {
    if (!currentUser?.token || currentUser.role !== 'admin') return;
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/roles`, {
        headers: authHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load roles');
      if (Array.isArray(data.roles) && data.roles.length > 0) {
        setAvailableRoles(data.roles);
      }
    } catch (error) {
      setAdminError(error.message);
    }
  }, [apiBaseUrl, authHeaders, currentUser?.role, currentUser?.token]);

  useEffect(() => {
    setModuleTab(isAdminScreen ? 'admin' : currentUser?.token ? 'stocks' : 'profile');
  }, [isAdminScreen, currentUser?.token]);

  useEffect(() => {
    if (!isAccountScreen) return;
    if (currentUser?.token) return;
    if (isCompactLogin) {
      setModuleTab('profile');
      setMode('login');
      return;
    }
    if (requestedAuthMode !== 'login' && requestedAuthMode !== 'register') return;
    setModuleTab('profile');
    setMode(requestedAuthMode);
  }, [requestedAuthMode, currentUser?.token, isAccountScreen, isCompactLogin]);

  useEffect(() => {
    if (!currentUser?.token) {
      setPreferredStocks([]);
      setAdminUsers([]);
      return;
    }

    if (isAccountScreen) {
      fetchPreferredStocks();
      return;
    }

    if (currentUser.role === 'admin') {
      fetchRoles();
      fetchAdminUsers();
    }
  }, [currentUser?.role, currentUser?.token, fetchAdminUsers, fetchPreferredStocks, fetchRoles, isAccountScreen]);

  useEffect(() => {
    if (!currentUser?.token) {
      setProfileForm({ username: '', email: '', currentPassword: '', newPassword: '', confirmPassword: '' });
      return;
    }

    setProfileForm((prev) => ({
      ...prev,
      username: currentUser.username || '',
      email: currentUser.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
  }, [currentUser?.token, currentUser?.username, currentUser?.email]);

  useEffect(() => {
    if (!isAccountScreen || currentUser?.token) return;

    const searchParams = new URLSearchParams(window.location.search);
    const paymentSessionId = searchParams.get('payment_session_id');
    const paymentCanceled = searchParams.get('payment_canceled') === '1';

    if (paymentCanceled) {
      setMode('register');
      setAuthError('Payment was canceled. You can review your plan and try again.');
      searchParams.delete('payment_canceled');
      searchParams.delete('auth');
      const nextQuery = searchParams.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`);
      return;
    }

    if (!paymentSessionId || paymentSessionHandledRef.current === paymentSessionId) return;

    paymentSessionHandledRef.current = paymentSessionId;
    setMode('register');
    setAuthLoading(true);
    setAuthError('');
    setAuthMessage('Finalizing your payment and creating the account...');

    (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/auth/register/payment-session/${paymentSessionId}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to finalize registration payment');
        if (!data.completed) throw new Error('Payment is not complete yet. Please wait a moment and try again.');

        onAuthChange({ ...data.user, token: data.token });
        const nextParams = new URLSearchParams(window.location.search);
        nextParams.delete('payment_session_id');
        nextParams.delete('auth');
        const nextQuery = nextParams.toString();
        window.history.replaceState({}, '', `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`);
      } catch (error) {
        setAuthError(error.message);
      } finally {
        setAuthLoading(false);
      }
    })();
  }, [apiBaseUrl, currentUser?.token, isAccountScreen, onAuthChange]);

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    setAuthMessage('');

    const endpoint = mode === 'register' ? '/api/auth/register' : '/api/auth/login';
    const selectedPaidTier = mode === 'register' && paidRegistrationTiers.includes(authForm.role);
    const payload =
      mode === 'register'
        ? {
            username: authForm.username,
            email: authForm.email,
            password: authForm.password,
            role: authForm.role
          }
        : {
            email: authForm.email,
            password: authForm.password
          };

    try {
      if (selectedPaidTier) {
        const paymentResponse = await fetch(`${apiBaseUrl}/api/auth/register/payment-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const paymentData = await paymentResponse.json();
        if (!paymentResponse.ok) throw new Error(paymentData.error || 'Unable to start payment');
        if (!paymentData.checkout_url) throw new Error('Payment link was not returned by the server');

        window.location.assign(paymentData.checkout_url);
        return;
      }

      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Authentication failed');

      onAuthChange({ ...data.user, token: data.token });
      const isPendingApproval = mode === 'register' && !data?.user?.is_approved;
      setAuthMessage(
        isPendingApproval
          ? 'Account created. An admin must approve your account before restricted advisor features are available.'
          : mode === 'register'
            ? 'Account created and signed in.'
            : 'Signed in successfully.'
      );
      setAuthForm({ username: '', email: '', password: '', role: 'start' });
      setModuleTab(isAccountScreen ? 'stocks' : 'admin');
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const addPreferredStock = async (event) => {
    event.preventDefault();
    if (!preferredInput.trim()) return;

    const normalizedSymbol = preferredInput.trim().toUpperCase();
    if (preferredStocks.some((stock) => String(stock).toUpperCase() === normalizedSymbol)) {
      setPreferredError('Preferred stock already exists for this user');
      return;
    }

    setPreferredLoading(true);
    setPreferredError('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/preferred-stocks`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ symbol: normalizedSymbol })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save preferred stock');
      const nextPreferredStocks = data.preferred_stocks || [];
      setPreferredStocks(nextPreferredStocks);
      if (onPreferredStocksChange) onPreferredStocksChange(nextPreferredStocks);
      setPreferredInput('');
    } catch (error) {
      setPreferredError(error.message);
    } finally {
      setPreferredLoading(false);
    }
  };

  const updateProfile = async (event) => {
    event.preventDefault();
    if (!currentUser?.token) return;

    setProfileLoading(true);
    setProfileError('');
    setProfileMessage('');

    const payload = {};
    const nextUsername = profileForm.username.trim();
    const nextEmail = profileForm.email.trim().toLowerCase();

    if (nextUsername && nextUsername !== currentUser.username) payload.username = nextUsername;
    if (nextEmail && nextEmail !== String(currentUser.email || '').toLowerCase()) payload.email = nextEmail;

    const wantsPasswordChange = Boolean(profileForm.newPassword || profileForm.currentPassword || profileForm.confirmPassword);
    if (wantsPasswordChange) {
      if (profileForm.newPassword !== profileForm.confirmPassword) {
        setProfileError('New password and confirm password must match');
        setProfileLoading(false);
        return;
      }
      payload.current_password = profileForm.currentPassword;
      payload.new_password = profileForm.newPassword;
    }

    if (Object.keys(payload).length === 0) {
      setProfileError('No changes to save');
      setProfileLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/profile`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update profile');

      onAuthChange({ ...data.user, token: currentUser.token });
      setProfileMessage('Profile updated successfully');
      setProfileForm((prev) => ({
        ...prev,
        username: data.user.username || prev.username,
        email: data.user.email || prev.email,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      setProfileError(error.message);
    } finally {
      setProfileLoading(false);
    }
  };

  const removePreferredStock = async (symbol) => {
    setPreferredLoading(true);
    setPreferredError('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/preferred-stocks/${symbol}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to remove preferred stock');
      const nextPreferredStocks = data.preferred_stocks || [];
      setPreferredStocks(nextPreferredStocks);
      if (onPreferredStocksChange) onPreferredStocksChange(nextPreferredStocks);
    } catch (error) {
      setPreferredError(error.message);
    } finally {
      setPreferredLoading(false);
    }
  };

  const toggleUserStatus = async (userId, isActive) => {
    setAdminLoading(true);
    setAdminError('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/users/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ is_active: isActive })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update user');
      await fetchAdminUsers();
    } catch (error) {
      setAdminError(error.message);
    } finally {
      setAdminLoading(false);
    }
  };

  const updateUserRole = async (userId, role) => {
    setAdminLoading(true);
    setAdminError('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/users/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ role })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update role');
      await fetchAdminUsers();
    } catch (error) {
      setAdminError(error.message);
    } finally {
      setAdminLoading(false);
    }
  };

  const updateUserApproval = async (userId, isApproved) => {
    setAdminLoading(true);
    setAdminError('');
    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/users/${userId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ is_approved: isApproved })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update approval');
      await fetchAdminUsers();
    } catch (error) {
      setAdminError(error.message);
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="mt-4 px-2 px-md-3" style={{ width: '100%', maxWidth: '100%' }}>
      <div
        className="rounded shadow-lg p-3 p-md-4"
        style={{
          background: darkTheme ? 'linear-gradient(180deg, #0f172a 0%, #111827 100%)' : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          border: darkTheme ? '1px solid #334155' : '1px solid #dbeafe'
        }}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
          <div>
            <h2 className="h4 fw-bold mb-1" style={{ color: darkTheme ? '#e2e8f0' : '#0f172a' }}>
              {isAdminScreen ? 'User Management' : currentUser?.token ? 'Account' : ''}
            </h2>
          </div>
          {null}
        </div>

          {isAccountScreen && currentUser?.token && !currentUser?.is_approved && (
            <div className="alert alert-warning mb-4">
              Your account is pending admin approval. Advisor analysis features and Preferred Stocks are disabled until approval.
            </div>
          )}

        {isAdminScreen && currentUser?.role !== 'admin' && (
          <div className="alert alert-danger mb-4">Admin access required for user management.</div>
        )}

        {!isCompactLogin && (isAdminScreen || (Boolean(currentUser?.token) && Boolean(currentUser?.is_approved))) && (
        <ul className="nav nav-pills mb-4 justify-content-center">
          {isAccountScreen && (
            <>
              <li className="nav-item">
                <button className={`nav-link ${moduleTab === 'stocks' ? 'active' : ''}`} onClick={() => setModuleTab('stocks')} type="button" disabled={!currentUser?.token || !currentUser?.is_approved}>
                  Preferred Stocks
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${moduleTab === 'profile' ? 'active' : ''}`} onClick={() => setModuleTab('profile')} type="button">
                  Profile
                </button>
              </li>
            </>
          )}
          {isAdminScreen && (
            <li className="nav-item">
              <button className={`nav-link ${moduleTab === 'admin' ? 'active' : ''}`} onClick={() => setModuleTab('admin')} type="button" disabled={currentUser?.role !== 'admin'}>
                Admin Users
              </button>
            </li>
          )}
        </ul>
        )}

        {isAccountScreen && moduleTab === 'profile' && !currentUser?.token && (
          <div className="row g-4 align-items-stretch">
            <div className="col-12">
              <div className="card h-100 shadow-sm" style={{ borderColor: darkTheme ? '#334155' : '#cbd5e1' }}>
                <div className="card-body p-4 p-md-5">
                  {!isCompactLogin && (
                  <div className="d-flex gap-2 mb-4 flex-wrap">
                    <button
                      type="button"
                      className={`btn btn-sm ${mode === 'login' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setMode('login')}
                    >
                      Sign In
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${mode === 'register' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setMode('register')}
                    >
                      Register
                    </button>
                  </div>
                  )}

                  {authError && <div className="alert alert-danger py-2">{authError}</div>}
                  {authMessage && <div className="alert alert-success py-2">{authMessage}</div>}

                  <form onSubmit={handleAuthSubmit} className="d-grid gap-4 mt-1">
                    {mode === 'register' && (
                      <div className="p-3 p-md-4 rounded" style={{ background: darkTheme ? '#0f172a' : '#eff6ff' }}>
                        <div className="fw-semibold mb-3">User Types</div>
                        <div className="small mb-3" style={{ color: darkTheme ? '#cbd5e1' : '#475569' }}>
                          Select your account type below. New accounts require admin approval before restricted advisor features are enabled.
                        </div>
                        <div className="row g-2 mb-3">
                          {registrationTiers.map((tier) => (
                            <div key={tier} className="col-6 col-md-4">
                              <button
                                type="button"
                                className={`w-100 btn btn-sm ${authForm.role === tier ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setAuthForm({ ...authForm, role: tier })}
                                style={{ minHeight: '38px', fontWeight: 600 }}
                              >
                                {formatRoleLabel(tier)}
                              </button>
                            </div>
                          ))}
                        </div>
                        {paidRegistrationTiers.includes(authForm.role) && (
                          <div className="small fw-semibold" style={{ color: darkTheme ? '#fcd34d' : '#9a3412' }}>
                            The {formatRoleLabel(authForm.role)} plan will continue to Stripe payment before the account is created.
                          </div>
                        )}
                      </div>
                    )}
                    {mode === 'register' && (
                      <div className="mb-1">
                        <label className="form-label">Username</label>
                        <input
                          className="form-control"
                          value={authForm.username}
                          onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
                          placeholder="Your name"
                          required
                        />
                      </div>
                    )}
                    <div className="mb-1">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={authForm.email}
                        onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                        placeholder="name@example.com"
                        required
                      />
                    </div>
                    <div className="mb-1">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={authForm.password}
                        onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                        placeholder="Minimum 8 characters"
                        required
                      />
                    </div>
                    <button className="btn btn-primary" type="submit" disabled={authLoading}>
                      {authLoading ? 'Working...' : mode === 'register' ? (paidRegistrationTiers.includes(authForm.role) ? 'Continue to Payment' : 'Create Account') : 'Sign In'}
                    </button>
                  </form>
                </div>
              </div>
            </div>

          </div>
        )}

        {isAccountScreen && currentUser?.token && currentUser?.is_approved && moduleTab === 'profile' && (
          <div className="row g-4">
            <div className="col-12 col-lg-5">
              <div className="card shadow-sm h-100" style={{ borderColor: darkTheme ? '#334155' : '#cbd5e1' }}>
                <div className="card-body">
                  <h5 className="fw-bold mb-2">Signed in as {currentUser.username}</h5>
                  <div className="small text-muted">{currentUser.email}</div>
                  <div className="small text-muted">Role: {currentUser.role}</div>
                  <div className="small text-muted">Status: {currentUser.is_active ? 'Enabled' : 'Disabled'}</div>
                  <div className="small text-muted">Approval: {currentUser.is_approved ? 'Approved' : 'Pending'}</div>
                  <button className="btn btn-outline-primary mt-3" type="button" onClick={() => setModuleTab('stocks')} disabled={!currentUser?.token || !currentUser?.is_approved}>
                    Manage Preferred Stocks
                  </button>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-7">
              <div className="card shadow-sm h-100" style={{ borderColor: darkTheme ? '#334155' : '#cbd5e1' }}>
                <div className="card-body">
                  <h5 className="fw-bold mb-3">Update Profile</h5>
                  {profileError && <div className="alert alert-danger py-2">{profileError}</div>}
                  {profileMessage && <div className="alert alert-success py-2">{profileMessage}</div>}
                  <form className="d-grid gap-3" onSubmit={updateProfile}>
                    <div>
                      <label className="form-label">Username</label>
                      <input
                        className="form-control"
                        value={profileForm.username}
                        onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className="form-control"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        required
                      />
                    </div>
                    <hr className="my-1" />
                    <div className="small text-muted">Change password (optional)</div>
                    <div>
                      <label className="form-label">Current Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={profileForm.currentPassword}
                        onChange={(e) => setProfileForm({ ...profileForm, currentPassword: e.target.value })}
                        placeholder="Required only when changing password"
                      />
                    </div>
                    <div>
                      <label className="form-label">New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={profileForm.newPassword}
                        onChange={(e) => setProfileForm({ ...profileForm, newPassword: e.target.value })}
                        placeholder="Minimum 8 characters"
                      />
                    </div>
                    <div>
                      <label className="form-label">Confirm New Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={profileForm.confirmPassword}
                        onChange={(e) => setProfileForm({ ...profileForm, confirmPassword: e.target.value })}
                        placeholder="Re-enter new password"
                      />
                    </div>
                    <button className="btn btn-primary" type="submit" disabled={profileLoading}>
                      {profileLoading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {isAccountScreen && currentUser?.token && currentUser?.is_approved && moduleTab === 'stocks' && (
          <div className="row g-4">
            <div className="col-12 col-lg-5">
              <div className="card shadow-sm h-100" style={{ borderColor: darkTheme ? '#334155' : '#cbd5e1' }}>
                <div className="card-body">
                  <h5 className="fw-bold mb-3">Add Preferred Stock</h5>
                  <form className="d-flex gap-2" onSubmit={addPreferredStock}>
                    <input
                      className="form-control"
                      value={preferredInput}
                      onChange={(e) => setPreferredInput(e.target.value.toUpperCase())}
                      placeholder="AAPL"
                      maxLength={10}
                    />
                    <button className="btn btn-primary" type="submit" disabled={preferredLoading}>
                      Add
                    </button>
                  </form>
                  {preferredError && <div className="alert alert-danger mt-3 py-2">{preferredError}</div>}
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-7">
              <div className="card shadow-sm h-100" style={{ borderColor: darkTheme ? '#334155' : '#cbd5e1' }}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Saved Stocks</h5>
                    <button className="btn btn-sm btn-outline-secondary" type="button" onClick={fetchPreferredStocks} disabled={preferredLoading}>
                      Refresh
                    </button>
                  </div>
                  {preferredLoading && <div className="text-muted small">Loading...</div>}
                  {!preferredLoading && preferredStocks.length === 0 && (
                    <div className="alert alert-info mb-0">No preferred stocks saved yet.</div>
                  )}
                  {!preferredLoading && preferredStocks.length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {preferredStocks.map((symbol) => (
                        <span 
                          key={symbol} 
                          className="badge text-bg-primary p-2 d-inline-flex align-items-center gap-2" 
                          style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                          onClick={() => {
                            setPreferredInput(symbol);
                            if (onSelectStock) onSelectStock(symbol);
                          }}
                          title="Click to populate input"
                        >
                          {symbol}
                          <button
                            type="button"
                            className="btn btn-sm btn-light py-0 px-1"
                            style={{ lineHeight: 1 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              removePreferredStock(symbol);
                            }}
                            title="Remove stock"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {isAdminScreen && currentUser?.role === 'admin' && moduleTab === 'admin' && (
          <div className="card shadow-sm" style={{ borderColor: darkTheme ? '#334155' : '#cbd5e1' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">User Administration</h5>
                <button className="btn btn-sm btn-outline-secondary" type="button" onClick={fetchAdminUsers} disabled={adminLoading}>
                  Refresh
                </button>
              </div>
              {adminError && <div className="alert alert-danger py-2">{adminError}</div>}
              {adminLoading && <div className="text-muted small">Loading users...</div>}
              {!adminLoading && adminUsers.length === 0 && <div className="alert alert-info mb-0">No users found.</div>}
              {!adminLoading && adminUsers.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Approval</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {adminUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.username}</td>
                          <td>{user.email}</td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={user.role}
                              onChange={(e) => updateUserRole(user.id, e.target.value)}
                              disabled={adminLoading}
                            >
                              {(availableRoles.includes(user.role) ? availableRoles : [...availableRoles, user.role]).map((role) => (
                                <option key={role} value={role}>
                                  {formatRoleLabel(role)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <span className={`badge ${user.is_active ? 'bg-success' : 'bg-secondary'}`}>
                              {user.is_active ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${user.is_approved ? 'bg-primary' : 'bg-warning text-dark'}`}>
                              {user.is_approved ? 'Approved' : 'Pending'}
                            </span>
                          </td>
                          <td className="text-end">
                            <button
                              type="button"
                              className={`btn btn-sm me-2 ${user.is_approved ? 'btn-outline-warning' : 'btn-outline-primary'}`}
                              onClick={() => updateUserApproval(user.id, !user.is_approved)}
                              disabled={adminLoading}
                            >
                              {user.is_approved ? 'Set Pending' : 'Approve'}
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${user.is_active ? 'btn-outline-danger' : 'btn-outline-success'}`}
                              onClick={() => toggleUserStatus(user.id, !user.is_active)}
                              disabled={adminLoading}
                            >
                              {user.is_active ? 'Disable' : 'Enable'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
